"use client";

import React, { useEffect, useMemo, useState } from "react";

import { useBoundariesEditMode } from "@/app/(internal)/panel/(maps)/_contexts/boundaries-edit-mode-context";
import { useBoundaryPanelActionsOptional } from "@/app/(internal)/panel/(maps)/map/_contexts/boundary-panel-actions-context";
import { Button } from "@/components/flowbite/button";
import { Card } from "@/components/flowbite/card";
import ListboxField from "@/components/flowbite/forms/fields/listbox/listbox-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/flowbite/tooltip";
import Typography from "@/components/flowbite/typography/typography";
import { GroupedSchoolSelectField } from "@/components/maps/grouped-school-select-field/grouped-school-select-field";
import { PolygonPreview } from "@/components/maps/polygon-preview";
import { dictionaries } from "@/dictionaries/content";
import { getBoundaryPolygonPreviewColors } from "@/providers/maps/utils/get-boundary-polygon-preview-colors";

import {
  BoundaryDistrictSchoolsProvider,
  useBoundaryDistrictSchools,
  type BoundarySchoolSelectVariant,
} from "../contexts/boundary-district-schools-context";
import { resolveBoundarySchoolDisplayFields } from "../utils/resolve-boundary-school-display.utils";

import { BoundaryPanelPlottingSection } from "./boundary-panel-plotting-section";
import { resolveBoundaryPanelDisplayName } from "./boundary-student-list-report.utils";
import { ConnectedSchoolRemoveButton } from "./connected-school-remove-button";
import { SectionBasicInfoList } from "./section-basic-info-list";
import { SectionTitle } from "./section-title";

import type { IBoundaryPayload } from "../../map-panel-context";

export type { BoundarySchoolSelectVariant };

const PREVIEW_SIZE = 40;

const EMPTY_SCHOOL_VALUE = "";

interface BoundaryPanelContentProps {
  payload: IBoundaryPayload | null;
  schoolSelectVariant?: BoundarySchoolSelectVariant;
}

interface BoundaryPanelContentBodyProps {
  payload: IBoundaryPayload;
  schoolSelectVariant: BoundarySchoolSelectVariant;
}

const BoundaryPanelContentBody: React.FC<BoundaryPanelContentBodyProps> = ({
  payload,
  schoolSelectVariant,
}) => {
  const dict = dictionaries.mapPage?.boundaryPanel;
  const actions = useBoundaryPanelActionsOptional();
  const { isBoundariesEditMode } = useBoundariesEditMode();
  const { schools, isLoading, schoolOptions } = useBoundaryDistrictSchools();
  const useSearchableSchoolSelect =
    schoolSelectVariant === "grouped" || schoolSelectVariant === "searchable";
  const groupSchoolsByType = schoolSelectVariant === "grouped";

  const boundaryPreviewTint = useMemo(
    () =>
      getBoundaryPolygonPreviewColors({
        schoolId: payload.schoolId,
        schoolInternalId: payload.schoolInternalId,
        schoolName: payload.schoolName,
        schoolTypes: payload.schoolTypes,
        color: payload.color,
      }),
    [
      payload.color,
      payload.schoolId,
      payload.schoolInternalId,
      payload.schoolName,
      payload.schoolTypes,
    ],
  );

  const selectedBoundaryLabel = dict?.selectedBoundary;
  const connectedSchoolLabel = dict?.connectedSchool;
  const schoolLabel = dict?.school;
  const schoolIdLabel = dict?.schoolId;
  const gradesLabel = dict?.grades;
  const addressLabel = dict?.address;
  const selectSchoolLabel = dict?.selectSchool ?? "Select school";
  const noSchoolLabel = dict?.noSchool ?? "No school";
  const boundaryDisplayName = resolveBoundaryPanelDisplayName(payload);

  const hasPreview = payload.previewCoordinates && payload.previewCoordinates.length > 0;
  const isSchoolConnectionDisabled = payload.isSchoolConnectionDisabled ?? false;
  const canEditSchool =
    !isSchoolConnectionDisabled &&
    isBoundariesEditMode &&
    !!payload.boundaryId &&
    (actions?.isUpdateBoundarySchoolActive ?? false);
  const showReadOnlySchool = !isSchoolConnectionDisabled && !canEditSchool;

  const selectedSchoolValue = payload.schoolInternalId ?? EMPTY_SCHOOL_VALUE;
  const boundaryId = payload.boundaryId;
  const hasLinkedSchool = selectedSchoolValue !== EMPTY_SCHOOL_VALUE;

  const assignedSchoolInternalIds = useMemo(() => {
    const links = payload.schools ?? [];
    if (links.length > 0) {
      return links.map((l) => l.schoolInternalId).filter((id) => id.trim() !== "");
    }

    const primary = payload.schoolInternalId?.trim() ?? "";
    return primary ? [primary] : [];
  }, [payload.schools, payload.schoolInternalId]);

  const [schoolSlots, setSchoolSlots] = useState<string[]>(
    assignedSchoolInternalIds.length > 0 ? assignedSchoolInternalIds : [""],
  );

  useEffect(() => {
    if (!canEditSchool) return;

    setSchoolSlots((prev) => {
      // Keep slot positions as the user arranged them; only drop unassigned, append new.
      const assignedSet = new Set(assignedSchoolInternalIds);
      const next = prev.map((id) => (id === "" || assignedSet.has(id) ? id : ""));
      const present = new Set(next.filter((id) => id !== ""));

      for (const id of assignedSchoolInternalIds) {
        if (!present.has(id)) {
          next.push(id);
          present.add(id);
        }
      }

      return next.length > 0 ? next : [""];
    });
  }, [assignedSchoolInternalIds, canEditSchool]);

  const { gradesRange, address } = resolveBoundarySchoolDisplayFields({
    schools,
    schoolId: payload.schoolId,
    schoolInternalId: payload.schoolInternalId,
    gradesRange: payload.gradesRange,
    address: payload.address,
  });

  const schoolDetailsItems = [
    { type: "text" as const, head: schoolIdLabel, value: payload.schoolInternalId || "--" },
    { type: "text" as const, head: gradesLabel, value: gradesRange ?? "--" },
    { type: "text" as const, head: addressLabel, value: address ?? "--" },
  ];

  /* eslint-disable react/no-array-index-key -- Editable empty school slots are intentionally position-addressed. */
  return (
    <div className="space-y-6 px-3 py-4">
      <div>
        <SectionTitle title={selectedBoundaryLabel} />
        <Card className="mt-2 flex w-full min-w-0 items-center gap-1 py-1 pl-3 pr-2">
          {hasPreview && (
            <PolygonPreview
              className="shrink-0"
              coordinates={payload.previewCoordinates!}
              size={PREVIEW_SIZE}
              {...boundaryPreviewTint}
            />
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="min-w-0 flex-1">
                <Typography.BodyMMedium
                  className="truncate"
                  colorShade={500}
                >
                  {boundaryDisplayName}
                </Typography.BodyMMedium>
              </div>
            </TooltipTrigger>
            <TooltipContent
              className="max-w-[360px] whitespace-normal"
              showArrow={false}
            >
              {boundaryDisplayName}
            </TooltipContent>
          </Tooltip>
        </Card>
      </div>

      {canEditSchool && (
        <div className="space-y-3">
          <div className="space-y-6">
            {schoolSlots.map((slotInternalId, slotIndex) => {
              const takenInternalIds = new Set(
                schoolSlots
                  .map((id, idx) => ({ id, idx }))
                  .filter(({ id, idx }) => idx !== slotIndex && id.trim() !== "")
                  .map(({ id }) => id),
              );
              const currentValue = slotInternalId;

              const filteredSchools = schools.filter((s) => {
                const id = s.internalId ?? "";
                return id === "" || !takenInternalIds.has(id);
              });

              const filteredSchoolOptions = schoolOptions.filter((opt) => {
                if (opt.value === EMPTY_SCHOOL_VALUE) return true;
                return !takenInternalIds.has(String(opt.value));
              });

              const selectedSchool = schools.find((s) => s.internalId === currentValue) ?? null;
              const slotGradesRange = selectedSchool?.grades?.lowestGradeLabel != null &&
                selectedSchool?.grades?.highestGradeLabel != null
                ? `${selectedSchool.grades.lowestGradeLabel}-${selectedSchool.grades.highestGradeLabel}`
                : null;

              const slotDetailsItems = [
                {
                  type: "text" as const,
                  head: schoolIdLabel,
                  value: currentValue ? currentValue : "--",
                },
                {
                  type: "text" as const,
                  head: gradesLabel,
                  value: slotGradesRange ?? "--",
                },
                {
                  type: "text" as const,
                  head: addressLabel,
                  value: selectedSchool?.address ?? "--",
                },
              ];

              const canRemoveThisSlot =
                schoolSlots.length > 1 || currentValue.trim() !== EMPTY_SCHOOL_VALUE;

              const handleSlotChange = (value: string) => {
                if (typeof boundaryId !== "string" || !actions?.isUpdateBoundarySchoolActive) {
                  return;
                }

                const next = [...schoolSlots];
                next[slotIndex] = value;
                setSchoolSlots(next);

                const selectedInternalIds = next.filter((id) => id.trim() !== "");
                const selectedSchools = selectedInternalIds
                  .map((id) => schools.find((s) => s.internalId === id))
                  .filter((s): s is (typeof schools)[number] => s != null);

                actions.updateBoundarySchool(boundaryId, selectedSchools);
              };

              const handleRemoveSlot = () => {
                if (typeof boundaryId !== "string" || !actions?.isUpdateBoundarySchoolActive) {
                  return;
                }

                const removedValue = schoolSlots[slotIndex] ?? "";
                if (schoolSlots.length === 1 && removedValue.trim() === "") {
                  return;
                }

                const next = schoolSlots.filter((_, idx) => idx !== slotIndex);
                const normalized = next.length > 0 ? next : [""];
                setSchoolSlots(normalized);

                if (removedValue.trim() !== "") {
                  const selectedInternalIds = normalized.filter((id) => id.trim() !== "");
                  const selectedSchools = selectedInternalIds
                    .map((id) => schools.find((s) => s.internalId === id))
                    .filter((s): s is (typeof schools)[number] => s != null);
                  actions.updateBoundarySchool(boundaryId, selectedSchools);
                }
              };

              return (
                <div
                  key={`${slotIndex}-${slotInternalId || "empty"}`}
                  className="group space-y-3"
                >
                  <SectionTitle
                    title={connectedSchoolLabel}
                    trailing={(
                      <ConnectedSchoolRemoveButton
                        ariaLabel={dict?.removeSchool ?? "Remove school"}
                        canRemove={canRemoveThisSlot}
                        onRemove={handleRemoveSlot}
                      />
                    )}
                  />

                  <div className="flex min-w-0 items-center gap-3">
                    <div className="w-[100px] min-w-[100px] max-w-[100px] shrink-0 pr-0">
                      <Typography.TableTitleXS colorShade={300}>
                        {schoolLabel}
                      </Typography.TableTitleXS>
                    </div>

                    <div className="min-w-0 flex-1">
                      {useSearchableSchoolSelect ? (
                        <GroupedSchoolSelectField
                          className="min-w-0 flex-1"
                          groupByType={groupSchoolsByType}
                          loading={isLoading}
                          noSchoolLabel={noSchoolLabel}
                          placeholder={selectSchoolLabel}
                          schools={filteredSchools}
                          value={currentValue}
                          onChange={handleSlotChange}
                        />
                      ) : (
                        <ListboxField
                          className="min-w-0 flex-1"
                          loading={isLoading}
                          options={filteredSchoolOptions}
                          placeholder={selectSchoolLabel}
                          value={currentValue}
                          onChange={handleSlotChange}
                        />
                      )}
                    </div>
                  </div>

                  <SectionBasicInfoList items={slotDetailsItems} />
                </div>
              );
            })}
          </div>

          <Button
            className="w-full"
            typography="bodyMMedium"
            variant="white"
            onClick={() => {
              if (!actions?.isUpdateBoundarySchoolActive || typeof boundaryId !== "string") return;
              setSchoolSlots((prev) => [...prev, ""]);
            }}
          >
            {dict?.addSchool ?? "Add another school connection"}
          </Button>
        </div>
      )}

      {showReadOnlySchool && (
        <div>
          <SectionTitle title={connectedSchoolLabel} />
          <SectionBasicInfoList
            items={
              hasLinkedSchool
                ? [
                  { type: "text", head: schoolLabel, value: boundaryDisplayName },
                  ...schoolDetailsItems,
                ]
                : [{ type: "text", head: schoolLabel, value: boundaryDisplayName }]
            }
          />
        </div>
      )}

      <BoundaryPanelPlottingSection payload={payload} />
    </div>
  );
  /* eslint-enable react/no-array-index-key */
};

export const BoundaryPanelContent: React.FC<BoundaryPanelContentProps> = React.memo(
  ({ payload, schoolSelectVariant = "searchable" }) => {
    const dict = dictionaries.mapPage?.boundaryPanel;

    if (!payload) return null;

    return (
      <BoundaryDistrictSchoolsProvider
        noSchoolLabel={dict?.noSchool ?? "No school"}
        schoolSelectVariant={schoolSelectVariant}
      >
        <BoundaryPanelContentBody
          key={payload.boundaryId ?? "no-boundary"}
          payload={payload}
          schoolSelectVariant={schoolSelectVariant}
        />
      </BoundaryDistrictSchoolsProvider>
    );
  },
);

BoundaryPanelContent.displayName = "BoundaryPanelContent";
